import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  checkoutSelectedPlanSelector,
  checkoutSelectedQuantitySelector,
  checkoutSelectedBillingPeriodSelector,
  checkoutExemptionSubmittedExternalCustomerSelector,
  couponCodeSelector,
  checkoutAddonsForEstimateSelector,
  checkoutSelectedCurrencySelector, checkoutCountryCodeSelector, checkoutPostalCodeSelector
} from '../modals/subscription_modal/payment_modal/subscription_checkout/selectors'
import {
  taxInformationModalConfirmedTaxInformationSelector,
} from '../selectors'

export const createSubscriptionEstimate = createAsyncThunk(
  'createSubscriptionEstimate',
  async (params, { getState, rejectWithValue }) => {
    const state = getState()
    const plan = checkoutSelectedPlanSelector(state)
    const quantity = checkoutSelectedQuantitySelector(state)
    const billingPeriod = checkoutSelectedBillingPeriodSelector(state)
    const externalCustomerId = checkoutExemptionSubmittedExternalCustomerSelector(state)
    const billingCountry = checkoutCountryCodeSelector(state)
    const postalCode = checkoutPostalCodeSelector(state)
    const couponCode = couponCodeSelector(state)
    const addons = checkoutAddonsForEstimateSelector(state)
    const currency = checkoutSelectedCurrencySelector(state)
    const confirmedTaxInfo = taxInformationModalConfirmedTaxInformationSelector(state)
    const countriesRequiringPostalCodeForEstimation = [
      'CA',
      'IN',
      'US'
    ]

    const taxInformation = () => {
      if (!confirmedTaxInfo) return {}

      const {
        stateProvince: taxFormStateProvince,
        postalCode: taxFormPostalCode,
        countryCode: taxFormCountryCode,
        australiaTaxId: taxFormAustraliaTaxId,
        canadaFederalTaxId: taxFormCanadaFederalTaxId,
        canadaProvincialTaxId: taxFormCanadaProvincialTaxId,
        taxId: taxFormTaxId
      } = confirmedTaxInfo

      switch (taxFormCountryCode) {
        case 'AU':
          return {
            tax_id: taxFormAustraliaTaxId,
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }
        case 'CA': {
          const result = {
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }

          if (taxFormCanadaFederalTaxId) result.tax_id = taxFormCanadaFederalTaxId
          if (taxFormCanadaProvincialTaxId) result.secondary_tax_id = taxFormCanadaProvincialTaxId

          return result
        }
        default:
          return {
            tax_id: taxFormTaxId,
            country_code: taxFormCountryCode,
            postal_code: taxFormPostalCode,
            ...(taxFormStateProvince ? { state_code: taxFormStateProvince } : {})
          }
      }
    }

    const includeBillingAddress = billingCountry
      && (!countriesRequiringPostalCodeForEstimation.includes((billingCountry)) || postalCode || params.provider === 'chargebee')

    const billingAddress = includeBillingAddress ? {
      postal_code: postalCode,
      country: billingCountry
    } : {}
    const data = {
      provider: params?.provider ?? 'chargebee',
      billing_information: {
        billing_address: billingAddress,
        tax_information: taxInformation()
      },
      billing_period: billingPeriod,
      plan: {
        id: plan,
        quantity
      },
      external_customer_id: externalCustomerId,
      coupon: couponCode,
      addons,
      currency
    }

    let withoutTaxInfoCall = false
    let withoutTaxInfoSuccess = null
    let withoutTaxInfoResponse = null

    if (data?.billing_information?.tax_information && Object.keys(data.billing_information.tax_information).length !== 0) {
      // Tax information exists, make API call without tax data so we can compare
      const dataWithoutTaxInfo = {
        ...data,
        billing_information: {
          billing_address: {
            postal_code: taxInformation().postal_code,
            country: taxInformation().country_code
          },
          tax_information: {}
        }
      }

      withoutTaxInfoCall = true
      const withoutTaxInfo = await httpRequest({
        url: '/api/billing/subscription_estimate',
        method: 'POST',
        data: dataWithoutTaxInfo
      }
      )

      withoutTaxInfoSuccess = withoutTaxInfo[0]
      withoutTaxInfoResponse = withoutTaxInfo[1]
    }

    const [success, response] = await httpRequest({
      url: '/api/billing/subscription_estimate',
      method: 'POST',
      data
    })

    if (!success || (withoutTaxInfoCall && !withoutTaxInfoSuccess)) {
      return rejectWithValue({
        type: 'subscriptionEstimate',
        message: I18n.t('publisher.billing.checkout.summary.estimation_error')
      })
    }

    return [response, withoutTaxInfoResponse]
  }
)
